"use client";

import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldLegend,
	FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { signUpWithEmailPassword } from "@/lib/auth/auth";
import { SITE_NAME } from "@/lib/constants";
import { EmailSchema, PasswordSchema, cn } from "@/lib/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { KeyIcon, UserIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as v from "valibot";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";

interface SignUpFormProps {
	email?: string;
	className?: string;
}

const schema = v.pipe(
	v.object({
		email: EmailSchema,
		password: PasswordSchema,
		confirmPassword: v.string(),
	}),
	v.forward(
		v.partialCheck(
			[["password"], ["confirmPassword"]],
			(input) => input.password === input.confirmPassword,
			"The passwords do not match.",
		),
		["confirmPassword"],
	),
);

export default function SignUpForm({ email, className }: SignUpFormProps) {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);

	const form = useForm({
		resolver: valibotResolver(schema),
		defaultValues: {
			email: email || "",
			password: "",
			confirmPassword: "",
		},
	});

	async function onSubmit(data: v.InferInput<typeof schema>) {
		setIsLoading(true);
		try {
			const { success, error } = await signUpWithEmailPassword(
				data.email,
				data.password,
			);
			if (error) throw error;
			toast("User successfully signed up", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});
		} catch (error: unknown) {
			console.error(error);
			toast("Error signing up. Please try again", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
		} finally {
			setIsLoading(false);

			// Update this route to redirect to an authenticated route.
			// The user already has an active session.
			router.push("/auth/sign-up-success");
		}
	}

	return (
		<Card className={cn("bg-transparent border-none shadow-none", className)}>
			<CardHeader className="items-center justify-center text-center space-y-4">
				<ZeekMapsIcon className="w-16 h-16 self-center" />
				<CardTitle className="text-3xl font-extrabold tracking-tighter">
					Sign Up to {SITE_NAME}
				</CardTitle>
				<CardDescription>Enter your email and password below.</CardDescription>
			</CardHeader>
			<CardContent>
				<form id="form-sign-up" onSubmit={form.handleSubmit(onSubmit)}>
					<FieldGroup>
						{/* Email */}
						<Controller
							name="email"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel
										htmlFor="form-sign-up-email"
										className="w-full font-bold"
									>
										Email
									</FieldLabel>
									<InputGroup className="rounded-full">
										<InputGroupInput
											id="form-sign-up-email"
											type="email"
											autoComplete="email"
											placeholder="z@example.com"
											aria-invalid={fieldState.invalid}
											{...field}
										/>
										<InputGroupAddon>
											<UserIcon />
										</InputGroupAddon>
									</InputGroup>

									{fieldState.invalid && (
										<FieldError errors={[fieldState.error]} />
									)}
								</Field>
							)}
						/>

						{/* Password */}
						<Controller
							name="password"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel
										htmlFor="form-update-password-password"
										className="w-full font-bold"
									>
										Password
									</FieldLabel>
									<InputGroup className="rounded-full">
										<InputGroupInput
											id="form-sign-up-password"
											type="password"
											autoComplete="password"
											placeholder="******"
											aria-invalid={fieldState.invalid}
											{...field}
										/>
										<InputGroupAddon>
											<KeyIcon />
										</InputGroupAddon>
									</InputGroup>

									{fieldState.invalid && (
										<FieldError errors={[fieldState.error]} />
									)}
								</Field>
							)}
						/>

						{/* Confirm Password */}
						<Controller
							name="confirmPassword"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel
										htmlFor="form-sign-up-confirm-password"
										className="w-full font-bold"
									>
										Confirm Password
									</FieldLabel>
									<InputGroup className="rounded-full">
										<InputGroupInput
											id="form-sign-up-confirm-password"
											type="password"
											autoComplete="password"
											placeholder="******"
											aria-invalid={fieldState.invalid}
											{...field}
										/>
										<InputGroupAddon>
											<KeyIcon />
										</InputGroupAddon>
									</InputGroup>

									{fieldState.invalid && (
										<FieldError errors={[fieldState.error]} />
									)}
								</Field>
							)}
						/>
					</FieldGroup>
				</form>
			</CardContent>

			<CardFooter>
				<div className="w-full flex flex-col space-y-2">
					<div className="w-full flex gap-2 items-center justify-end">
						<Button
							size="default"
							type="submit"
							className="w-full rounded-full"
							form="form-sign-up"
							disabled={isLoading}
						>
							Sign Up
						</Button>
					</div>
					<div className="text-center text-sm">
						Already have an account?{" "}
						<Link
							href="/auth/sign-in"
							className="font-semibold text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
						>
							Login
						</Link>
					</div>
				</div>
			</CardFooter>
		</Card>
	);
}

export function SignUpForm2({ email, className }: SignUpFormProps) {
	const router = useRouter();

	const [emailId, setEmailId] = useState(email || "");
	const [password, setPassword] = useState("");
	const [repeatPassword, setRepeatPassword] = useState("");

	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const handleSignUp = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setError(null);

		if (password !== repeatPassword) {
			setError("Passwords do not match");
			setIsLoading(false);
			return;
		}

		try {
			const { success, error } = await signUpWithEmailPassword(
				emailId,
				password,
			);
			if (error) throw error;

			toast("User successfully signed up", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});

			setTimeout(() => {
				router.push("/auth/sign-up-success");
			}, 1000);
		} catch (error: unknown) {
			setError(error instanceof Error ? error.message : "An error occurred");
			toast("Error signing up. Please try again", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			<ZeekMapsIcon className="w-16 h-16 self-center" />
			<form onSubmit={handleSignUp} className="">
				<FieldSet className="rounded-xl bg-background flex flex-col gap-2">
					<FieldLegend className="mt-4 text-xl font-bold text-primary text-center">
						<h2 className="my-4 text-3xl font-extrabold tracking-tighter text-primary">
							Sign Up to {SITE_NAME}
						</h2>
					</FieldLegend>
					<FieldGroup className="my-10">
						<Field className="gap-0">
							<FieldLabel
								htmlFor="email"
								className="block text-sm/6 font-medium text-primary"
							>
								Email address
							</FieldLabel>
							<Input
								id="email"
								name="email"
								type="email"
								required
								autoComplete="email"
								placeholder="z@example.com"
								value={emailId}
								onChange={(e) => setEmailId(e.target.value)}
								className={cn(
									"mt-2 block w-full rounded-lg border border-primary/25 bg-background p-3 text-sm/6 text-primary",
									"focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-primary/25",
								)}
							/>
						</Field>
						<Field className="gap-0">
							<FieldLabel
								htmlFor="password"
								className="block text-sm/6 font-medium text-primary"
							>
								Password
							</FieldLabel>
							<Input
								id="password"
								name="password"
								type="password"
								required
								autoComplete="current-password"
								autoFocus
								onChange={(e) => setPassword(e.target.value)}
								className={cn(
									"mt-2 block w-full rounded-lg border border-primary/25 bg-primary/5 p-3 text-sm/6 text-primary",
									"focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-primary/25",
								)}
							/>
						</Field>
						<Field className="gap-0">
							<FieldLabel
								htmlFor="repeat-password"
								className="block text-sm/6 font-medium text-primary"
							>
								Repeat Password
							</FieldLabel>
							<Input
								id="repeat-password"
								name="repeat-password"
								type="password"
								required
								autoComplete="current-password"
								onChange={(e) => setRepeatPassword(e.target.value)}
								className={cn(
									"mt-2 block w-full rounded-lg border border-primary/25 bg-primary/5 p-3 text-sm/6 text-primary",
									"focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-primary/25",
								)}
							/>
						</Field>
					</FieldGroup>
					{error && <p className="text-sm text-red-500">{error}</p>}
					<Field orientation="horizontal">
						<Button
							type="submit"
							disabled={isLoading}
							className={cn(
								"flex w-full justify-center rounded-full bg-primary p-3 text-md font-semibold text-background shadow-sm",
								"hover:bg-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
								isLoading && "cursor-not-allowed opacity-50",
							)}
						>
							{isLoading ? "Creating an account..." : "Sign Up"}
						</Button>
					</Field>

					<div className="text-center text-sm">
						Already have an account?{" "}
						<Link
							href="/auth/sign-in"
							className="font-semibold text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
						>
							Login
						</Link>
					</div>
				</FieldSet>
			</form>
		</div>
	);
}
